import type { ElementTransformer } from "@lexical/markdown";
import { DecoratorNode, type LexicalNode, type SerializedLexicalNode } from "lexical";
import type { Component } from "vue";
import { h } from "vue";

import { DEACTIVATED_USER_LABEL } from "~/components/visualization/constants";
import { EntityName } from "~/shared/enums";
import { useDataStore, useEnvironmentStore } from "~/stores";
import { getTitleOrNoTitle } from "~/utils/entityTitle";

import { createBlockNode } from "../markdownRules";
import DartLink from "./DartLink.vue";

export type SerializedDartLinkNode = SerializedLexicalNode & {
  link: string;
  kind: EntityName;
  duid: string | undefined;
  label: string;
  labelOverride: string | undefined;
};

const getLabel = (title: string | undefined, kind: EntityName | undefined) => {
  if (title !== undefined) {
    return getTitleOrNoTitle(title);
  }
  if (kind === EntityName.USER) {
    return DEACTIVATED_USER_LABEL;
  }
  return `(Unknown${kind === undefined ? "" : ` ${kind.toLowerCase()}`})`;
};

export class DartLinkNode extends DecoratorNode<Component> {
  __link: string;

  __kind: EntityName;

  __duid: string | undefined;

  __label: string;

  __labelOverride: string | undefined;

  __initialized: boolean = false;

  static getType(): string {
    return "dart-link";
  }

  static clone(node: DartLinkNode): DartLinkNode {
    return new DartLinkNode(node.__link, node.__kind, node.__duid, node.__labelOverride, node.__key);
  }

  constructor(link: string, kind: EntityName, duid?: string, labelOverride?: string, key?: string) {
    super(key);
    this.__link = link;
    this.__kind = kind;
    this.__label = getLabel(undefined, kind);
    this.__duid = duid;
    this.__labelOverride = labelOverride;
  }

  getLink(): string {
    return this.__link;
  }

  getKind(): EntityName {
    return this.__kind;
  }

  getDuid(): string | undefined {
    return this.__duid;
  }

  private initializeFromStore(): void {
    if (this.__initialized || !this.__duid) {
      return;
    }
    const dataStore = useDataStore();
    const entity = dataStore.getEntity(this.__duid, this.__kind);
    this.__label = getLabel(entity?.title, this.__kind);
    this.__initialized = true;
  }

  getLabel(): string {
    if (this.__labelOverride) {
      return this.__labelOverride;
    }
    this.initializeFromStore();
    return this.__label;
  }

  getFullLink(): string {
    const environmentStore = useEnvironmentStore();
    return `${environmentStore.urlBase}${this.getLink()}`;
  }

  // eslint-disable-next-line class-methods-use-this
  createDOM(): HTMLElement {
    const element = document.createElement("div");
    element.classList.add("contents");
    return element;
  }

  // eslint-disable-next-line class-methods-use-this
  updateDOM(): false {
    return false;
  }

  static importJSON(serializedNode: SerializedDartLinkNode): LexicalNode {
    return new DartLinkNode(
      serializedNode.link,
      serializedNode.kind,
      serializedNode.duid,
      serializedNode.labelOverride
    );
  }

  exportJSON(): SerializedDartLinkNode {
    return {
      type: DartLinkNode.getType(),
      link: this.getLink(),
      kind: this.getKind(),
      duid: this.getDuid(),
      label: this.getLabel(),
      labelOverride: this.__labelOverride,
      version: 1,
    };
  }

  decorate(): Component {
    return h(DartLink, {
      link: this.getLink(),
      name: this.getKind(),
      duid: this.getDuid(),
      labelOverride: this.__labelOverride,
    });
  }

  // eslint-disable-next-line class-methods-use-this
  isInline(): true {
    return true;
  }

  // eslint-disable-next-line class-methods-use-this
  isKeyboardSelectable(): false {
    return false;
  }

  getTextContent(): string {
    return `[${this.getLabel()}](${this.getFullLink()})`;
  }
}

export function $createDartLinkNode(
  link: string,
  kind: EntityName,
  duid: string | undefined,
  labelOverride?: string
): DartLinkNode {
  return new DartLinkNode(link, kind, duid, labelOverride);
}

export function $isDartLinkNode(node?: LexicalNode): node is DartLinkNode {
  return node instanceof DartLinkNode;
}

// TODO improve this to have good markdown content, make alternate versions for other custom nodes, and then start using them
export const DART_LINK_TRANSFORMER: ElementTransformer = {
  dependencies: [DartLinkNode],
  export: (node: LexicalNode) => {
    if (!$isDartLinkNode(node)) {
      return null;
    }
    return `<${node.getKind}: ${node.getDuid}>`;
  },
  regExp: /^(?!)\s/,
  replace: createBlockNode((match: string[]) => $createDartLinkNode(match[0], match[1] as EntityName, match[2])),
  type: "element",
};
